import intl from "react-intl-universal";
import { formatMonetaryValue } from "../../../Utils/formatters";
import {
  BRL_CENTS_SCALE,
  CRYPTO_DISPLAY_DECIMALS,
  CRYPTO_SCALE,
  brlCentsToNumber,
  calcBrlFromCrypto,
  calcCryptoFromBrl,
  cryptoScaledToNumber,
  formatCryptoAmount,
  parseFoxBitDecimal,
} from "../../../Services/stablesMoney";

export const STABLES_HOME_PATH = "/stables";
export const STABLES_DEPOSIT_PATH = "/stables/depositar";
export const STABLES_HISTORY_PATH = "/stables/historico";

/** Fired after IB `POST /v1/stables/wallets` so every `useStablesBalances` instance reloads. */
export const STABLES_WALLET_CREATED_EVENT = "valut:stables-wallet-created";

export const STABLES_HISTORY_TAB_IDS = ["all", "purchases", "sales", "swaps", "withdrawals"];

export const isValidStablesHistoryTab = (tabId) =>
  STABLES_HISTORY_TAB_IDS.includes(String(tabId || "").trim());

export const stablesHistoryTabPath = (tabId) =>
  tabId === "all" ? STABLES_HOME_PATH : `${STABLES_HISTORY_PATH}/${tabId}`;

export const OPERATION_BREADCRUMB_LABELS = {
  buy: "Comprar",
  sell: "Vender",
  swap: "Swap",
  withdraw: "Sacar",
};

export const STABLE_ASSETS = [
  { id: "usdc", label: "USDC" },
  { id: "usdt", label: "USDT" },
  { id: "eurc", label: "EURC" },
  { id: "brl1", label: "BRL1" },
];

/** Assets accepted by IB `POST /v1/stables/wallets` (Baas create). */
export const STABLES_CREATE_ASSETS = ["USDC", "USDT", "EURC", "BRL1"];

/** Short labels for Baas `NetworkCode` values in create-wallet UI. */
export const STABLES_CREATE_NETWORK_LABELS = {
  POLYGON: "Polygon",
  ERC20: "Ethereum",
  SOLANA: "Solana",
  TRON: "Tron",
  BASE: "Base",
};

/** Preferred chip order when listing networks for an asset. */
export const STABLES_CREATE_NETWORK_ORDER = [
  "POLYGON",
  "ERC20",
  "SOLANA",
  "TRON",
  "BASE",
];

/**
 * Canonical asset → networks fallback (aligns with StableAssetNetwork /
 * CorrectStableAssetNetworksAndWipeLedger.sql / CreateStableAssetNetworkSchema.sql).
 * Prefer API `GET …/assets/{asset}/networks` when available.
 */
export const STABLES_CREATE_NETWORKS_BY_ASSET = {
  BRL1: ["POLYGON"],
  USDT: ["ERC20", "TRON", "POLYGON"],
  USDC: ["ERC20", "POLYGON", "SOLANA"],
  EURC: ["ERC20", "BASE"],
};

/** @deprecated Flat list — use getStablesCreateNetworksForAsset(asset). */
export const STABLES_CREATE_NETWORKS = STABLES_CREATE_NETWORK_ORDER.filter(
  (code) => STABLES_CREATE_NETWORK_LABELS[code]
).map((id) => ({ id, label: STABLES_CREATE_NETWORK_LABELS[id] }));

export const DEFAULT_STABLES_CREATE_ASSET = "USDC";
export const DEFAULT_STABLES_CREATE_NETWORK = "POLYGON";

export const normalizeStablesCreateAsset = (raw) =>
  String(raw || "").trim().toUpperCase();

/** Uppercase catalog codes the account already has a stables wallet for. */
export const getOwnedStablesCreateAssetCodes = (assets = []) => {
  const owned = new Set();
  (Array.isArray(assets) ? assets : []).forEach((asset) => {
    const code = normalizeStablesCreateAsset(
      asset?.id || asset?.label || asset?.asset
    );
    if (STABLES_CREATE_ASSETS.includes(code)) owned.add(code);
  });
  return owned;
};

/** Enabled create-catalog assets that still need a wallet on this account. */
export const getMissingStablesCreateAssets = (assets = []) => {
  const owned = getOwnedStablesCreateAssetCodes(assets);
  return STABLES_CREATE_ASSETS.filter((code) => !owned.has(code));
};

export const pickDefaultStablesCreateAsset = (available, preferred) => {
  const raw = Array.isArray(available) ? available : null;
  if (raw && raw.length === 0) return "";

  const list = (raw ?? STABLES_CREATE_ASSETS)
    .map(normalizeStablesCreateAsset)
    .filter((code) => STABLES_CREATE_ASSETS.includes(code));
  const pref = normalizeStablesCreateAsset(preferred);
  if (pref && list.includes(pref)) return pref;
  return list[0] || "";
};

const normalizeCreateNetworkCode = (raw) => {
  const key = String(raw || "")
    .trim()
    .toUpperCase();
  if (!key) return "";
  if (STABLES_CREATE_NETWORK_LABELS[key]) return key;
  const aliases = {
    ETHEREUM: "ERC20",
    ETH: "ERC20",
    "ERC-20": "ERC20",
    MATIC: "POLYGON",
    POL: "POLYGON",
    TRC20: "TRON",
    "TRC-20": "TRON",
    SOL: "SOLANA",
  };
  return aliases[key] || key;
};

const sortCreateNetworkCodes = (codes = []) => {
  const unique = [
    ...new Set(codes.map(normalizeCreateNetworkCode).filter(Boolean)),
  ];
  return unique.sort((a, b) => {
    const ia = STABLES_CREATE_NETWORK_ORDER.indexOf(a);
    const ib = STABLES_CREATE_NETWORK_ORDER.indexOf(b);
    const ra = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
    const rb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
};

/** UI label with DB NetworkCode, e.g. `Polygon [POLYGON]` / `Ethereum [ERC20]`. */
export const getStablesCreateNetworkLabel = (networkCode) => {
  const code = normalizeCreateNetworkCode(networkCode);
  if (!code) return "";
  const name = STABLES_CREATE_NETWORK_LABELS[code];
  if (!name) return String(networkCode || "").trim() || code;
  return `${name} [${code}]`;
};

/** Networks for create-wallet chips for the given asset (fallback catalog). */
export const getStablesCreateNetworksForAsset = (asset) => {
  const symbol = String(asset || "")
    .trim()
    .toUpperCase();
  const codes = STABLES_CREATE_NETWORKS_BY_ASSET[symbol] || [];
  return sortCreateNetworkCodes(codes).map((id) => ({
    id,
    label: getStablesCreateNetworkLabel(id),
  }));
};

/** Map API network rows → create-wallet chip options; falls back to catalog. */
export const resolveStablesCreateNetworks = (asset, apiNetworks) => {
  const fromApi = (Array.isArray(apiNetworks) ? apiNetworks : [])
    .map((item) => {
      const id = normalizeCreateNetworkCode(
        item?.id || item?.network_code || item?.networkCode || item?.code
      );
      if (!id) return null;
      // Prefer Name [NetworkCode] for known codes (API may send "Ethereum (ERC-20)").
      return {
        id,
        label: getStablesCreateNetworkLabel(id),
      };
    })
    .filter(Boolean);

  if (fromApi.length > 0) {
    const byId = new Map(fromApi.map((n) => [n.id, n]));
    return sortCreateNetworkCodes([...byId.keys()]).map(
      (id) => byId.get(id) || { id, label: getStablesCreateNetworkLabel(id) }
    );
  }

  return getStablesCreateNetworksForAsset(asset);
};

export const pickDefaultStablesCreateNetwork = (networks, currentNetwork) => {
  const list = Array.isArray(networks) ? networks : [];
  if (list.length === 0) return "";
  const current = normalizeCreateNetworkCode(currentNetwork);
  if (current && list.some((n) => n.id === current)) return current;
  return list[0].id;
};

/** BRL notional for FoxBit display quotes — 1 BRL yields unit price from the API `price` field. */
export const DISPLAY_MARKET_QUOTE_AMOUNT = 1;

/** Default stablecoin pre-selected in buy/sell/swap flows. */
export const DEFAULT_STABLE_ASSET_ID = "usdc";

/** Fixed order for the quotes carousel (BRL1 → USDC → USDT). */
export const STABLE_QUOTES_CAROUSEL_ASSET_IDS = ["brl1", "usdc", "usdt"];

/** Lowercase stablecoin ids supported by Baas / FoxBit markets API. */
export const STABLE_MARKET_ASSET_IDS = STABLE_ASSETS.map((asset) => asset.id);

export const isStableMarketAsset = (assetId) =>
  STABLE_MARKET_ASSET_IDS.includes(String(assetId || "").trim().toLowerCase());

export const isQuotesCarouselAsset = (assetId) =>
  STABLE_QUOTES_CAROUSEL_ASSET_IDS.includes(String(assetId || "").trim().toLowerCase());

/** Sparkline window options for mini chart (maps to API `window` param). */
export const STABLES_SPARKLINE_WINDOWS = [
  { id: "hour", badgeKey: "stables.sparklineWindowHour", labelKey: "stables.sparklineLastHour" },
  { id: "week", badgeKey: "stables.sparklineWindowWeek", labelKey: "stables.sparklineLastWeek" },
  { id: "month", badgeKey: "stables.sparklineWindowMonth", labelKey: "stables.sparklineLastMonth" },
];

export const OPERATION_TABS = [
  { id: "buy", label: "Comprar" },
  { id: "sell", label: "Vender" },
  { id: "swap", label: "Swap" },
  { id: "withdraw", label: "Sacar" },
];

export const getHistoryTabs = () => [
  { id: "purchases", label: intl.get("stables.tabPurchases") },
  { id: "sales", label: intl.get("stables.tabSales") },
  { id: "swaps", label: intl.get("stables.tabSwaps") },
  { id: "withdrawals", label: intl.get("stables.tabWithdrawals") },
];

export const getGeneralHistoryTabs = () => [
  { id: "all", label: intl.get("common.all") },
  ...getHistoryTabs(),
];

/** @deprecated Use getHistoryTabs() at render time for i18n */
export const HISTORY_TABS = [
  { id: "purchases", label: "Compras" },
  { id: "sales", label: "Vendas" },
  { id: "swaps", label: "Conversões" },
  { id: "withdrawals", label: "Saques" },
];

/** @deprecated Use getGeneralHistoryTabs() at render time for i18n */
export const GENERAL_HISTORY_TABS = [
  { id: "all", label: "Todos" },
  ...HISTORY_TABS,
];

export const getHistoryKindMeta = () => ({
  purchases: { label: intl.get("stables.typePurchase"), badgeClass: "bg-sky-50 text-sky-700" },
  sales: { label: intl.get("stables.typeSale"), badgeClass: "bg-amber-50 text-amber-700" },
  swaps: { label: intl.get("stables.typeSwap"), badgeClass: "bg-violet-50 text-violet-700" },
  withdrawals: { label: intl.get("stables.typeWithdrawal"), badgeClass: "bg-gray-100 text-gray-700" },
  deposits: { label: intl.get("stables.typeDeposit"), badgeClass: "bg-teal-50 text-teal-700" },
  transfers: { label: intl.get("stables.typeTransfer"), badgeClass: "bg-indigo-50 text-indigo-700" },
});

/** @deprecated Use getHistoryKindMeta() at render time for i18n */
export const HISTORY_KIND_META = {
  purchases: { label: "Compra", badgeClass: "bg-sky-50 text-sky-700" },
  sales: { label: "Venda", badgeClass: "bg-amber-50 text-amber-700" },
  swaps: { label: "Conversão", badgeClass: "bg-violet-50 text-violet-700" },
  withdrawals: { label: "Saque", badgeClass: "bg-gray-100 text-gray-700" },
};

export const parseStablesDate = (dateStr) => {
  const [datePart, timePart = "00:00"] = dateStr.split(" ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

export const EMPTY_STABLES_HISTORY_TAB_COUNTS = {
  all: 0,
  purchases: 0,
  sales: 0,
  swaps: 0,
  withdrawals: 0,
};

export const formatBrl = (value) => formatMonetaryValue(value, "BRL");

/** Formats crypto qty; optional second arg appends asset symbol (USDT/USDC/…). */
export const formatStable = (value, assetId) => {
  const amount = formatCryptoAmount(value, CRYPTO_DISPLAY_DECIMALS);
  const label = String(assetId || "").trim();
  if (!label) return amount;
  // Prefer catalog label when id matches; otherwise uppercase the token.
  const catalog = STABLE_ASSETS.find((a) => a.id === label.toLowerCase());
  return `${amount} ${catalog?.label || label.toUpperCase()}`;
};

export const BRL_AMOUNT_INPUT_DECIMALS = 2;
/** Stable input precision — match crypto display so fills/balance checks stay consistent. */
export const STABLE_AMOUNT_INPUT_DECIMALS = CRYPTO_DISPLAY_DECIMALS;
/** Max whole-number digits for stable amount inputs (prevents JS float overflow). */
export const STABLE_AMOUNT_MAX_INTEGER_DIGITS = 12;
export const BRL_AMOUNT_MAX_INTEGER_DIGITS = 12;

const limitAmountIntegerDigits = (intPart, currencyMode = "brl") => {
  const maxDigits =
    currencyMode === "stable"
      ? STABLE_AMOUNT_MAX_INTEGER_DIGITS
      : BRL_AMOUNT_MAX_INTEGER_DIGITS;
  return intPart.slice(0, maxDigits);
};

/** Strips leading zeros from integer part; keeps lone "0" or empty while typing. */
const normalizeIntegerPart = (intPart, currencyMode = "brl") => {
  if (intPart === "") return "";
  const limited = limitAmountIntegerDigits(intPart, currencyMode);
  if (limited === "") return "";
  return limited.replace(/^0+/, "") || "0";
};

/** @typedef {"brl" | "stable"} AmountCurrencyMode */

/** Placeholder shown in amount inputs per currency mode. */
export const getAmountInputPlaceholder = (currencyMode = "brl") =>
  currencyMode === "stable"
    ? `0.${"0".repeat(STABLE_AMOUNT_INPUT_DECIMALS)}`
    : "0,00";

/**
 * Numeric value formatted for amount inputs.
 * BRL: pt-BR locale, comma decimal, 2 places (e.g. 1.234,56).
 * Stable: en-US locale, period decimal, 6 places (e.g. 1,234.567890).
 */
export const formatAmountInput = (value, currencyMode = "brl") => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;

  if (currencyMode === "stable") {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: STABLE_AMOUNT_INPUT_DECIMALS,
      maximumFractionDigits: STABLE_AMOUNT_INPUT_DECIMALS,
    }).format(safeValue);
  }

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: BRL_AMOUNT_INPUT_DECIMALS,
    maximumFractionDigits: BRL_AMOUNT_INPUT_DECIMALS,
  }).format(safeValue);
};

/**
 * Floors a numeric amount to `decimals` places (never rounds up).
 * Used for % balance fills so the amount never exceeds available.
 */
export const floorAmountToDecimals = (value, decimals) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  const places = Math.max(0, Math.floor(Number(decimals) || 0));
  const factor = 10 ** places;
  return Math.floor(numeric * factor + Number.EPSILON) / factor;
};

/**
 * Formats a % fill for amount inputs, flooring to currency decimals first
 * so 25/50/75/100% never round above available balance.
 */
export const formatPercentAmountInput = (value, currencyMode = "brl") => {
  const decimals =
    currencyMode === "stable" ? STABLE_AMOUNT_INPUT_DECIMALS : BRL_AMOUNT_INPUT_DECIMALS;
  return formatAmountInput(floorAmountToDecimals(value, decimals), currencyMode);
};

/**
 * Sanitizes raw keystroke/paste input into a display-safe amount string while typing.
 * Does not add thousand separators — use formatAmountInput on blur for that.
 */
export const sanitizeAmountInput = (raw, currencyMode = "brl") => {
  if (raw === "" || raw === null || raw === undefined) return "";

  const maxDecimals =
    currencyMode === "stable" ? STABLE_AMOUNT_INPUT_DECIMALS : BRL_AMOUNT_INPUT_DECIMALS;
  const decimalSep = currencyMode === "stable" ? "." : ",";

  let s = String(raw).replace(/[^\d,.]/g, "");
  if (!s) return "";

  const commaCount = (s.match(/,/g) || []).length;
  const periodCount = (s.match(/\./g) || []).length;

  if (commaCount === 0 && periodCount === 0) {
    return normalizeIntegerPart(s, currencyMode);
  }

  const buildResult = (intPart, decPart, endsWithSep) => {
    const limitedInt = normalizeIntegerPart(intPart, currencyMode);
    if (decPart) return `${limitedInt}${decimalSep}${decPart}`;
    if (endsWithSep) return `${limitedInt}${decimalSep}`;
    return limitedInt;
  };

  if (commaCount > 0 && periodCount > 0) {
    const lastComma = s.lastIndexOf(",");
    const lastPeriod = s.lastIndexOf(".");
    const decIdx =
      currencyMode === "brl"
        ? (lastComma > lastPeriod ? lastComma : lastPeriod)
        : (lastPeriod > lastComma ? lastPeriod : lastComma);

    const intPart = limitAmountIntegerDigits(
      s.slice(0, decIdx).replace(/[^\d]/g, ""),
      currencyMode
    );
    const decPart = s.slice(decIdx + 1).replace(/[^\d]/g, "").slice(0, maxDecimals);
    const endsWithSep = s.endsWith(",") || s.endsWith(".");
    return buildResult(intPart, decPart, endsWithSep);
  }

  if (currencyMode === "brl") {
    if (commaCount > 0) {
      s = s.replace(/\./g, "");
      const commaIdx = s.indexOf(",");
      const intPart = s.slice(0, commaIdx).replace(/[^\d]/g, "");
      const decPart = s.slice(commaIdx + 1).replace(/[^\d]/g, "").slice(0, maxDecimals);
      return buildResult(intPart, decPart, s.endsWith(","));
    }

    if (periodCount === 1) {
      const periodIdx = s.indexOf(".");
      const after = s.slice(periodIdx + 1);
      if (after.length <= maxDecimals) {
        const intPart = s.slice(0, periodIdx).replace(/[^\d]/g, "");
        const decPart = after.replace(/[^\d]/g, "").slice(0, maxDecimals);
        return buildResult(intPart, decPart, s.endsWith("."));
      }
    }

    return normalizeIntegerPart(s.replace(/[.,]/g, ""), currencyMode);
  }

  if (periodCount > 0) {
    s = s.replace(/,/g, "");
    const periodIdx = s.indexOf(".");
    const intPart = s.slice(0, periodIdx).replace(/[^\d]/g, "");
    const decPart = s.slice(periodIdx + 1).replace(/[^\d]/g, "").slice(0, maxDecimals);
    return buildResult(intPart, decPart, s.endsWith("."));
  }

  if (commaCount === 1) {
    const commaIdx = s.indexOf(",");
    const after = s.slice(commaIdx + 1);
    if (after.length <= maxDecimals) {
      const intPart = s.slice(0, commaIdx).replace(/[^\d]/g, "");
      const decPart = after.replace(/[^\d]/g, "").slice(0, maxDecimals);
      return buildResult(intPart, decPart, s.endsWith(","));
    }
  }

  return normalizeIntegerPart(s.replace(/,/g, ""), currencyMode);
};

/**
 * Parses user-typed amount strings into a number.
 * Handles both BRL (1.234,56) and stable (1,234.5678) separators.
 */
export const parseAmount = (value, currencyMode = "brl") => {
  if (value === "" || value === null || value === undefined) return 0;

  const str = String(value).trim();
  if (!str) return 0;

  const hasComma = str.includes(",");
  const hasPeriod = str.includes(".");

  if (hasComma && hasPeriod) {
    const lastComma = str.lastIndexOf(",");
    const lastPeriod = str.lastIndexOf(".");
    if (lastComma > lastPeriod) {
      const normalized = str.replace(/\./g, "").replace(",", ".");
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const normalized = str.replace(/,/g, "");
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (hasComma) {
    if (currencyMode === "brl") {
      const normalized = str.replace(",", ".");
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    const commaIndex = str.lastIndexOf(",");
    const afterComma = str.slice(commaIndex + 1);
    const isThousandsComma =
      afterComma.length === 3 && commaIndex > 0 && !str.slice(0, commaIndex).includes(",");

    if (isThousandsComma) {
      const normalized = str.replace(/,/g, "");
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    const normalized = str.replace(",", ".");
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (hasPeriod) {
    if (currencyMode === "brl") {
      const parts = str.split(".");
      const lastPart = parts[parts.length - 1];
      if (parts.length > 1 && lastPart.length === 3) {
        const normalized = str.replace(/\./g, "");
        const parsed = parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
      }
    }
    const parsed = parseFloat(str);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const parsed = parseFloat(str);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getAsset = (id) => STABLE_ASSETS.find((a) => a.id === id);

export const getAssetLabel = (assetId) => {
  const asset = getAsset(assetId);
  if (asset?.label) return asset.label;

  const normalized = String(assetId || "").trim();
  return normalized ? normalized.toUpperCase() : "";
};

export const getStablesStatusLabel = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("process")) return intl.get("stables.statusProcessing");
  return intl.get("stables.statusCompleted");
};

export const formatWithdrawalDestination = (destination) => {
  if (!destination) return "";

  const mask = destination.match(/••••\w+/)?.[0] || "";
  const lower = destination.toLowerCase();

  if (lower.includes("banc")) {
    return intl.get("stables.destinationBankAccount", { mask });
  }

  if (lower.includes("carteira") || lower.includes("wallet") || lower.includes("cartera")) {
    return intl.get("stables.destinationExternalWallet", { mask });
  }

  return destination;
};

export const formatPurchaseHistoryTitle = (item) =>
  intl.get("stables.historyPurchaseTitle", {
    asset: getAssetLabel(item.asset),
    amount: formatStable(item.amountStable, item.asset),
  });

export const formatSaleHistoryTitle = (item) =>
  intl.get("stables.historySaleTitle", {
    asset: getAssetLabel(item.from),
    amount: formatStable(item.amountFrom, item.from),
  });

export const formatSwapHistoryTitle = (item) =>
  intl.get("stables.historySwapTitle", {
    from: getAssetLabel(item.from),
    to: getAssetLabel(item.to),
  });

export const formatWithdrawalHistoryTitle = (item) =>
  intl.get("stables.historyWithdrawalTitle", {
    asset: getAssetLabel(item.asset),
    amount: formatStable(item.amount, item.asset),
  });

export const formatDepositHistoryTitle = (item) =>
  intl.get("stables.historyDepositTitle", {
    asset: getAssetLabel(item.asset),
    amount: formatStable(item.amount, item.asset),
  });

export const formatTransferHistoryTitle = (item) =>
  intl.get("stables.historyTransferTitle", {
    asset: getAssetLabel(item.asset),
    amount: formatStable(item.amount, item.asset),
  });

export const resolveWithdrawalPlatformFee = (item) => {
  if (item?.feeStable != null) return Number(item.feeStable) || 0;
  return 0;
};

export const formatWithdrawalHistoryAmount = (item) =>
  formatStable(item.amount, item.asset);

export const formatWithdrawalHistoryFeeDetail = (item) =>
  intl.get("stables.feeAmount", {
    amount: formatStable(resolveWithdrawalPlatformFee(item), item.asset),
  });

/** Normaliza valor digitado em BRL e stable conforme moeda de entrada (bruto, sem taxa) */
export const resolveTradeAmounts = ({ operation, inputCurrency, numericAmount, asset, priceString }) => {
  if (!asset || !numericAmount) {
    return { brlAmount: 0, stableAmount: 0 };
  }

  const effectivePrice = priceString ?? asset.priceString;

  if (operation === "buy" && effectivePrice) {
    if (inputCurrency === "brl") {
      const brlCents = parseFoxBitDecimal(numericAmount.toFixed(2), BRL_CENTS_SCALE);
      const cryptoScaled = calcCryptoFromBrl(brlCents, effectivePrice);
      return {
        brlAmount: numericAmount,
        stableAmount: cryptoScaledToNumber(cryptoScaled),
      };
    }

    const cryptoScaled = parseFoxBitDecimal(String(numericAmount), CRYPTO_SCALE);
    const brlCents = calcBrlFromCrypto(cryptoScaled, effectivePrice);
    return {
      stableAmount: numericAmount,
      brlAmount: brlCentsToNumber(brlCents),
    };
  }

  if (operation === "buy") {
    if (inputCurrency === "brl") {
      return { brlAmount: numericAmount, stableAmount: numericAmount / asset.rate };
    }
    return { stableAmount: numericAmount, brlAmount: numericAmount * asset.rate };
  }

  if (inputCurrency === "stable") {
    return { stableAmount: numericAmount, brlAmount: numericAmount * asset.rate };
  }

  return { brlAmount: numericAmount, stableAmount: numericAmount / asset.rate };
};

export const resolveSwapAmounts = ({ fromAsset, toAsset, numericAmount }) => {
  if (!fromAsset || !toAsset || !numericAmount) {
    return { fromAmount: 0, toAmountGross: 0 };
  }

  const toAmountGross = numericAmount * (fromAsset.rate / toAsset.rate);
  return { fromAmount: numericAmount, toAmountGross };
};

/** Aplica taxa e retorna valores líquidos + detalhamento da taxa */
export const applyStablesFee = ({
  operation,
  inputCurrency = "brl",
  priceString,
  brlAmount = 0,
  stableAmount = 0,
  asset,
  fromAsset,
  toAsset,
  fromAmount = 0,
  toAmountGross = 0,
  feeConfig = {},
}) => {
  const feePercent = feeConfig[operation] ?? 0;
  const feeRate = feePercent / 100;

  if (operation === "buy" && asset) {
    const effectivePrice = priceString ?? asset.priceString;

    if (inputCurrency === "stable") {
      const feeStable = stableAmount * feeRate;
      let feeBrl;

      if (effectivePrice) {
        const feeCryptoScaled = parseFoxBitDecimal(String(feeStable), CRYPTO_SCALE);
        feeBrl = brlCentsToNumber(calcBrlFromCrypto(feeCryptoScaled, effectivePrice));
      } else {
        feeBrl = feeStable * asset.rate;
      }

      return {
        operation,
        inputCurrency,
        feePercent,
        feeBrl,
        feeStable,
        feeAssetLabel: asset.label,
        brlAmount,
        stableAmount,
        netBrlAmount: brlAmount + feeBrl,
        netStableAmount: stableAmount,
      };
    }

    let feeBrl = brlAmount * feeRate;
    let feeStable = effectivePrice
      ? cryptoScaledToNumber(
          calcCryptoFromBrl(
            parseFoxBitDecimal(feeBrl.toFixed(2), BRL_CENTS_SCALE),
            effectivePrice
          )
        )
      : feeBrl / (asset.rate || 1);
    let netStableAmount = stableAmount - feeStable;

    if (effectivePrice && brlAmount > 0) {
      const brlCents = parseFoxBitDecimal(brlAmount.toFixed(2), BRL_CENTS_SCALE);
      const feeBrlCents = (brlCents * BigInt(Math.round(feeRate * 10000))) / 10000n;
      const grossCrypto = calcCryptoFromBrl(brlCents, effectivePrice);
      const feeCrypto = calcCryptoFromBrl(feeBrlCents, effectivePrice);
      feeBrl = brlCentsToNumber(feeBrlCents);
      feeStable = cryptoScaledToNumber(feeCrypto);
      netStableAmount = cryptoScaledToNumber(grossCrypto - feeCrypto);
    }

    return {
      operation,
      inputCurrency,
      feePercent,
      feeBrl,
      feeStable,
      feeAssetLabel: asset.label,
      brlAmount,
      stableAmount,
      netBrlAmount: brlAmount,
      netStableAmount,
    };
  }

  if (operation === "sell" && asset) {
    const feeBrl = brlAmount * feeRate;
    const feeStable = feeBrl / asset.rate;
    return {
      operation,
      feePercent,
      feeBrl,
      feeStable,
      feeAssetLabel: asset.label,
      brlAmount,
      stableAmount,
      netBrlAmount: brlAmount - feeBrl,
      netStableAmount: stableAmount,
    };
  }

  if (operation === "swap" && fromAsset && toAsset) {
    const feeToAmount = toAmountGross * feeRate;
    const feeFromAmount = feeToAmount * (toAsset.rate / fromAsset.rate);
    return {
      operation,
      feePercent,
      feeBrl: feeToAmount * toAsset.rate,
      feeStable: feeToAmount,
      feeAssetLabel: toAsset.label,
      fromAmount,
      toAmountGross,
      netFromAmount: fromAmount,
      netToAmount: toAmountGross - feeToAmount,
      fromAssetLabel: fromAsset.label,
      toAssetLabel: toAsset.label,
    };
  }

  return {
    operation,
    feePercent: 0,
    feeBrl: 0,
    feeStable: 0,
    netBrlAmount: brlAmount,
    netStableAmount: stableAmount,
  };
};

export const computeTradeTransaction = (params) => {
  const gross = resolveTradeAmounts(params);
  return applyStablesFee({
    operation: params.operation,
    inputCurrency: params.inputCurrency,
    priceString: params.asset?.priceString,
    brlAmount: gross.brlAmount,
    stableAmount: gross.stableAmount,
    asset: params.asset,
    feeConfig: params.feeConfig,
  });
};

/** Campos de exibição da cotação de compra conforme moeda de entrada. */
export const resolveBuyQuoteDisplay = (quote = {}) => {
  const isQuantityInput = quote.inputCurrency === "stable";

  return {
    stableAmount: isQuantityInput ? quote.stableAmount : quote.netStableAmount,
    brlAmount: isQuantityInput ? quote.netBrlAmount : quote.brlAmount,
  };
};

export const computeSwapTransaction = ({ fromAsset, toAsset, numericAmount, feeConfig }) => {
  const gross = resolveSwapAmounts({ fromAsset, toAsset, numericAmount });
  return applyStablesFee({
    operation: "swap",
    fromAsset,
    toAsset,
    fromAmount: gross.fromAmount,
    toAmountGross: gross.toAmountGross,
    feeConfig,
  });
};

const calcPercentFeeScaled = (amountScaled, feePercent) => {
  const feeRateScaled = BigInt(Math.round(Number(feePercent) * 10000));
  return (amountScaled * feeRateScaled) / 1000000n;
};

export const computeWithdrawTransaction = ({ asset, chain, amount, destination, travelRule, feeConfig = {} }) => {
  if (!asset || !chain || !amount) {
    return null;
  }

  const amountNum = Number(amount);
  if (!Number.isFinite(amountNum) || amountNum <= 0) {
    return null;
  }

  const feePercent = feeConfig.withdraw ?? 0;
  const cryptoScaled = parseFoxBitDecimal(
    amountNum.toFixed(STABLE_AMOUNT_INPUT_DECIMALS),
    CRYPTO_SCALE
  );
  if (cryptoScaled <= 0n) {
    return null;
  }

  const platformFeeScaled = calcPercentFeeScaled(cryptoScaled, feePercent);
  const networkFeeStable = Number(chain.networkFeeStable) || 0;
  const networkFeeScaled = parseFoxBitDecimal(
    networkFeeStable.toFixed(CRYPTO_DISPLAY_DECIMALS),
    CRYPTO_SCALE
  );
  const totalFeeScaled = platformFeeScaled + networkFeeScaled;
  const netScaled = cryptoScaled > totalFeeScaled ? cryptoScaled - totalFeeScaled : 0n;

  const stableAmount = cryptoScaledToNumber(cryptoScaled);
  const platformFeeStable = cryptoScaledToNumber(platformFeeScaled);
  const netStableAmount = cryptoScaledToNumber(netScaled);
  const totalFeeStable = cryptoScaledToNumber(totalFeeScaled);

  return {
    operation: "withdraw",
    feePercent,
    feeStable: platformFeeStable,
    networkFeeStable,
    networkFeeBrl: networkFeeStable * asset.rate,
    totalFeeStable,
    stableAmount,
    netStableAmount,
    brlEquivalent: stableAmount * asset.rate,
    netBrlEquivalent: netStableAmount * asset.rate,
    chain: chain.label,
    chainId: chain.id,
    destination: destination?.trim() || "",
    travelRule: travelRule || null,
    assetLabel: asset.label,
    assetId: asset.id,
  };
};

export const numericAmountToCryptoScaled = (numericAmount) => {
  const numeric = Number(numericAmount);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0n;
  return parseFoxBitDecimal(numeric.toFixed(STABLE_AMOUNT_INPUT_DECIMALS), CRYPTO_SCALE);
};

export const numericAmountToBrlCents = (numericAmount) => {
  const numeric = Number(numericAmount);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0n;
  return parseFoxBitDecimal(numeric.toFixed(BRL_AMOUNT_INPUT_DECIMALS), BRL_CENTS_SCALE);
};

export const balanceToCryptoScaled = (balance) => {
  const numeric = Number(balance);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0n;
  return parseFoxBitDecimal(formatCryptoAmount(numeric, CRYPTO_DISPLAY_DECIMALS), CRYPTO_SCALE);
};

export const balanceToBrlCents = (balance) => {
  const numeric = Number(balance);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0n;
  return parseFoxBitDecimal(numeric.toFixed(BRL_AMOUNT_INPUT_DECIMALS), BRL_CENTS_SCALE);
};

/** `/accounts/balance` returns integer centavos (e.g. 243831 = R$ 2.438,31). */
export const accountBalanceCentsToBrlScaled = (balanceCents) => {
  const numeric = Number(balanceCents);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0n;
  return BigInt(Math.round(numeric));
};

const buyLimitAvailableCentsScaled = (buyLimitAvailableCents = 0) =>
  BigInt(Math.max(0, Math.round(Number(buyLimitAvailableCents) || 0)));

/**
 * Effective BRL for buy: min(account balance centavos, compra limit disponível).
 * Limit of 0 means unlimited (do not invent a balance from the limit alone).
 */
export const resolveEffectiveBuyBrlCents = (brlBalanceCents, buyLimitAvailableCents) => {
  const balanceCents = accountBalanceCentsToBrlScaled(brlBalanceCents);
  if (balanceCents <= 0n) return 0n;

  const limitCents = buyLimitAvailableCentsScaled(buyLimitAvailableCents);
  if (limitCents <= 0n) return balanceCents;
  return balanceCents < limitCents ? balanceCents : limitCents;
};

/** Formatted BRL available for buy (min of account balance and compra limit). */
export const resolveBuyAvailableBrlDisplay = (
  brlBalanceCents,
  buyLimitAvailableCents,
  brlDisplayFallback
) => {
  const effectiveCents = resolveEffectiveBuyBrlCents(
    brlBalanceCents,
    buyLimitAvailableCents
  );
  if (effectiveCents <= 0n) {
    return brlDisplayFallback ?? formatBrl(0);
  }
  return formatBrl(brlCentsToNumber(effectiveCents));
};

/** Formatted BRL disponível from compra limit (totalCents - usedCents). */
export const formatBuyLimitAvailableDisplay = (buyLimitAvailableCents = 0) =>
  formatBrl(brlCentsToNumber(buyLimitAvailableCentsScaled(buyLimitAvailableCents)));

const resolveMaxPurchasableStableScaled = (
  selectedAsset,
  brlBalanceCents,
  buyLimitAvailableCents = 0
) => {
  const brlAvailableCents = resolveEffectiveBuyBrlCents(
    brlBalanceCents,
    buyLimitAvailableCents
  );
  const priceString = selectedAsset.priceString;

  if (priceString && brlAvailableCents > 0n) {
    return calcCryptoFromBrl(brlAvailableCents, priceString);
  }

  if (selectedAsset.rate > 0 && brlAvailableCents > 0n) {
    const brlReais = brlCentsToNumber(brlAvailableCents);
    return numericAmountToCryptoScaled(brlReais / selectedAsset.rate);
  }

  return 0n;
};

/** Max stable purchasable from min(account balance, compra limit) at current rate. */
export const resolveMaxPurchasableStableDisplay = (
  selectedAsset,
  brlBalanceCents,
  buyLimitAvailableCents = 0
) => {
  const maxStableScaled = resolveMaxPurchasableStableScaled(
    selectedAsset,
    brlBalanceCents,
    buyLimitAvailableCents
  );
  return formatStable(
    maxStableScaled > 0n ? cryptoScaledToNumber(maxStableScaled) : 0
  );
};

const resolveMaxBuyStableFromLimitScaled = (selectedAsset, buyLimitAvailableCents = 0) => {
  const limitCents = buyLimitAvailableCentsScaled(buyLimitAvailableCents);
  if (limitCents <= 0n) return 0n;

  const priceString = selectedAsset.priceString;
  if (priceString) {
    return calcCryptoFromBrl(limitCents, priceString);
  }

  if (selectedAsset.rate > 0) {
    const brlReais = brlCentsToNumber(limitCents);
    return numericAmountToCryptoScaled(brlReais / selectedAsset.rate);
  }

  return 0n;
};

/** Max stable from compra limit disponível at current rate (not capped by account balance). */
export const resolveMaxBuyStableFromLimitDisplay = (
  selectedAsset,
  buyLimitAvailableCents = 0
) => {
  const maxStableScaled = resolveMaxBuyStableFromLimitScaled(
    selectedAsset,
    buyLimitAvailableCents
  );
  return formatStable(
    maxStableScaled > 0n ? cryptoScaledToNumber(maxStableScaled) : 0
  );
};

/**
 * BRL reais base for buy % chips: min(account balance, compra limit disponível).
 * Limit 0 = unlimited → use full account balance.
 */
export const resolveBuyPercentBaseBrl = (
  brlBalanceCents,
  buyLimitAvailableCents = 0
) =>
  brlCentsToNumber(
    resolveEffectiveBuyBrlCents(brlBalanceCents, buyLimitAvailableCents)
  );

/** True when a compra limit policy exists (total/available > 0), vs unlimited. */
export const isBuyLimitConfigured = (limit = {}) => {
  const totalCents = Number(limit.totalCents ?? limit.total_cents ?? limit.max ?? 0) || 0;
  const availableCents = Number(
    limit.availableCents ?? limit.available_cents ?? 0
  );
  if (Number.isFinite(availableCents) && availableCents > 0) return true;
  return totalCents > 0;
};

const resolveSellAvailableDisplay = (selectedAsset, inputCurrency) => {
  if (inputCurrency === "brl") {
    return formatBrl(selectedAsset.balance * selectedAsset.rate);
  }

  return formatStable(selectedAsset.balance);
};

/** Returns whether the entered amount exceeds available balance for the active operation. */
export const resolveInsufficientBalance = ({
  operation,
  inputCurrency = "brl",
  numericAmount,
  brlBalance = 0,
  buyLimitAvailableCents = 0,
  selectedAsset,
  fromAsset,
  tradeTransaction,
  swapTransaction,
  withdrawTransaction,
  brlDisplay,
}) => {
  if (!numericAmount || numericAmount <= 0) {
    return { insufficient: false };
  }

  const brlAvailableCents = resolveEffectiveBuyBrlCents(
    brlBalance,
    buyLimitAvailableCents
  );
  const formatBuyAvailableBrl = () =>
    resolveBuyAvailableBrlDisplay(brlBalance, buyLimitAvailableCents, brlDisplay);

  if (operation === "buy") {
    if (!selectedAsset) return { insufficient: false };

    if (tradeTransaction) {
      const payableBrl =
        inputCurrency === "stable" ? tradeTransaction.netBrlAmount : tradeTransaction.brlAmount;
      const requiredCents = numericAmountToBrlCents(payableBrl);
      if (requiredCents > brlAvailableCents) {
        return {
          insufficient: true,
          availableDisplay:
            inputCurrency === "stable"
              ? resolveMaxPurchasableStableDisplay(
                  selectedAsset,
                  brlBalance,
                  buyLimitAvailableCents
                )
              : formatBuyAvailableBrl(),
        };
      }
      return { insufficient: false };
    }

    if (inputCurrency === "brl") {
      const requiredCents = numericAmountToBrlCents(numericAmount);
      if (requiredCents > brlAvailableCents) {
        return {
          insufficient: true,
          availableDisplay: formatBuyAvailableBrl(),
        };
      }
      return { insufficient: false };
    }

    const maxStableScaled = resolveMaxPurchasableStableScaled(
      selectedAsset,
      brlBalance,
      buyLimitAvailableCents
    );
    const requiredStableScaled = numericAmountToCryptoScaled(numericAmount);
    if (requiredStableScaled > maxStableScaled) {
      return {
        insufficient: true,
        availableDisplay: resolveMaxPurchasableStableDisplay(
          selectedAsset,
          brlBalance,
          buyLimitAvailableCents
        ),
      };
    }

    return { insufficient: false };
  }

  if (operation === "sell") {
    if (!selectedAsset) return { insufficient: false };

    const stableNeeded =
      tradeTransaction?.stableAmount ??
      (inputCurrency === "stable"
        ? numericAmount
        : selectedAsset.rate > 0
          ? numericAmount / selectedAsset.rate
          : 0);

    const requiredScaled = numericAmountToCryptoScaled(stableNeeded);
    const availableScaled = balanceToCryptoScaled(selectedAsset.balance);

    if (requiredScaled > availableScaled) {
      return {
        insufficient: true,
        availableDisplay: resolveSellAvailableDisplay(selectedAsset, inputCurrency),
      };
    }

    return { insufficient: false };
  }

  if (operation === "swap") {
    if (!fromAsset) return { insufficient: false };

    const fromAmount = swapTransaction?.fromAmount ?? numericAmount;
    const requiredScaled = numericAmountToCryptoScaled(fromAmount);
    const availableScaled = balanceToCryptoScaled(fromAsset.balance);

    if (requiredScaled > availableScaled) {
      return {
        insufficient: true,
        availableDisplay: formatStable(fromAsset.balance, fromAsset.id),
      };
    }

    return { insufficient: false };
  }

  if (operation === "withdraw") {
    if (!selectedAsset) return { insufficient: false };

    const stableNeeded = withdrawTransaction?.stableAmount ?? numericAmount;
    const requiredScaled = numericAmountToCryptoScaled(stableNeeded);
    const availableScaled = balanceToCryptoScaled(selectedAsset.balance);

    if (requiredScaled > availableScaled) {
      return {
        insufficient: true,
        availableDisplay: formatStable(selectedAsset.balance, selectedAsset.id),
      };
    }

    return { insufficient: false };
  }

  return { insufficient: false };
};
